import { ChangeEventHandler, useCallback, useState } from 'react';
import { components as ReactSelectCommponents } from 'react-select';
import { Select, Button, TextField } from '@nike/eds';
import { Plus } from '@nike/nike-design-system-icons';
import { useAtom } from 'jotai';
import * as R from 'ramda';
import { useNavigate, useLoaderData, useRevalidator } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import classnames from 'classnames';
import { arrayMove } from '@dnd-kit/sortable';
import RankingRuleTriggerModal from 'components/RankingRule/RankingRuleTriggerModal';
import RankingRuleTargetModal from 'components/RankingRule/RankingRuleTargetModal';
import RankingRuleBoostsOrBuries from 'components/RankingRule/RankingRuleBoostsOrBuries';
import TriggerConceptSection from 'components/RankingRule/TriggerConceptSection';
import TriggerKeywordSection from 'components/RankingRule/TriggerKeywordSection';
import {
  rankingRuleTriggerModalAtom,
  rankingRuleTargetModalAtom,
  rankingRuleEditResult,
} from '../../atoms/ranking';
import { QUERY_KEYS } from 'constants/index';
import RankingClient from '../../clientAPI/ranking';
import { validateRankingRulePayload } from '../../utils/dataValidator';
import { isExistScope } from '../../utils/authHelpers';
import {
  marketplaceOptions,
  channelOptions,
  navigationTypeOptions,
  strategyOptions,
  createRankingRulePayload,
} from '../../utils/rankingRuleHelpers';
import { IconButton } from './IconButton';
import { RankingRuleActionType } from 'types/ranking';
import { Toggle } from 'components/form/Toggle';
import { ConfirmationModal } from './ConfirmationModal';

export default function RankingRuleCreateForm({
  data,
}: {
  data?: RankingRule;
}) {
  const navigate = useNavigate();
  const loaderData = useLoaderData() as { access_token: string };
  const revalidator = useRevalidator();
  const queryClient = useQueryClient();
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:ranking:write', jwt);

  const [ruleName, setRuleName] = useState('');
  const [priority, setPriority] = useState(1);
  const [strategy, setStrategy] = useState(strategyOptions[0]);
  const [consumerChannels, setConsumerChannels] = useState<
    { value: string; label: string }[]
  >([]);
  const [navigationType, setNavigationType] = useState<
    { value: string; label: string }[]
  >([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isApplyToAll, setIsApplyToAll] = useState(false);
  const [triggerModal, setTriggerModal] = useAtom(rankingRuleTriggerModalAtom);
  const [targetModal, setTargetModal] = useAtom(rankingRuleTargetModalAtom);
  const [updateResult, setUpdateResult] = useAtom(rankingRuleEditResult);
  const [validationErr, setValidationErr] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: (data: RankingRule): Promise<RankingRule> =>
      RankingClient.createRankingRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.RANKING_RULE_LIST],
      });
      setUpdateResult((result) => {
        result.resultMsg = 'Created';
        result.hasError = false;
      });
      navigate('/ranking');
    },
    onError: async (err: keyable) => {
      setUpdateResult((result) => {
        result.resultMsg = err.message || JSON.stringify(err);
        result.hasError = true;
      });
      err.status === 401 && revalidator.revalidate();
    },
  });

  const onTriggerMenuClick = (type: string) => {
    return () => {
      setTriggerModal((modal) => {
        modal.visible = true;
        modal.type = type;
      });
    };
  };
  const onBoostItemRemove = (idx: number) => {
    setTargetModal((modalData) => {
      modalData.boosts = R.remove(idx, 1, modalData.boosts);
    });
  };
  const onBuryItemRemove = (idx: number) => {
    setTargetModal((modalData) => {
      modalData.buries = R.remove(idx, 1, modalData.buries);
    });
  };
  const onBoostItemReOrder = (idx: number, targetIdx: number) => {
    setTargetModal((modalData) => {
      modalData.boosts = arrayMove(modalData.boosts, idx, targetIdx);
    });
  };
  const onBuryItemReOrder = (idx: number, targetIdx: number) => {
    setTargetModal((modalData) => {
      modalData.buries = arrayMove(modalData.buries, idx, targetIdx);
    });
  };
  const [
    isBoostRemoveAllConfirmModalOpen,
    setIsBoostRemoveAllConfirmModalOpen,
  ] = useState(false);

  const onConfirmBoostRemoveAll = () => {
    setTargetModal((modalData) => {
      modalData.boosts = [];
    });
    setIsBoostRemoveAllConfirmModalOpen(false);
  };
  const onDismissBoostRemoveAll = () => {
    setIsBoostRemoveAllConfirmModalOpen(false);
  };
  const onBoostRemoveAll = () => {
    setIsBoostRemoveAllConfirmModalOpen(true);
  };
  const [isBuryRemoveAllConfirmModalOpen, setIsBuryRemoveAllConfirmModalOpen] =
    useState(false);
  const onConfirmBuryRemoveAll = () => {
    setTargetModal((modalData) => {
      modalData.buries = [];
    });
    setIsBuryRemoveAllConfirmModalOpen(false);
  };
  const onDismissBuryRemoveAll = () => {
    setIsBuryRemoveAllConfirmModalOpen(false);
  };
  const onBuryRemoveAll = () => {
    setIsBuryRemoveAllConfirmModalOpen(true);
  };
  const handleCancel = () => {
    navigate('/ranking');
  };
  const [isApplyToAllConfirmModalOpen, setIsApplyToAllConfirmModalOpen] =
    useState(false);
  const applyToAllToggleOnChange: ChangeEventHandler<HTMLInputElement> =
    useCallback(
      (_e) => {
        if (!isApplyToAll) {
          setIsApplyToAllConfirmModalOpen(true);
        } else {
          setIsApplyToAll(false);
        }
      },
      [isApplyToAll],
    );
  const onConfirmApplyToAllModal = useCallback(() => {
    setIsApplyToAll(true);
    setTriggerModal((modal) => {
      modal.cascadingConcepts = [];
      modal.exactConcepts = [];
      modal.exactKeywords = [];
      modal.partialKeywords = [];
    });
    setIsApplyToAllConfirmModalOpen(false);
  }, [setTriggerModal]);
  const onDismissApplyToAllModal = useCallback(() => {
    setIsApplyToAllConfirmModalOpen(false);
  }, []);
  const handleSubmit = () => {
    const payload: RankingRule = createRankingRulePayload({
      ruleName,
      consumerChannels,
      navigationType,
      priority,
      strategy,
      startDate,
      endDate,
      triggerModal,
      targetModal,
      isApplyToAll,
    });

    console.log(payload);
    const validation = validateRankingRulePayload(payload);
    if (!validation.success) {
      const err = validation.error.issues.map((i) => i.message);
      return setValidationErr(err);
    }
    mutation.mutate(payload);
  };
  return (
    <section className='tw-max-w-7xl tw-mx-auto'>
      {mutation.status === 'loading' && (
        <div className='tw-fixed tw-bottom-[50%] tw-left-[50%]'>
          <div className='eds-spinner eds-spinner--large' />
        </div>
      )}
      <div
        className={classnames({
          'tw-opacity-40': mutation.status === 'loading',
        })}
      >
        <div
          className='tw-grid tw-gap-2 tw-py-2'
          style={{
            gridTemplateColumns: '4fr 1fr',
          }}
        >
          <TextField
            id='product-ranking-rule'
            label='Rule Name'
            value={ruleName}
            onChange={(event) => setRuleName(event.target.value)}
          />
          <TextField
            id='product-ranking-rule-priority'
            label='Rule Priority'
            type='number'
            value={priority}
            onChange={(event) => {
              const p = Math.abs(parseInt(event.target.value));
              setPriority(p);
            }}
          />
        </div>

        <Select
          id='product-ranking-rule-strategy-select'
          label='Strategy'
          options={strategyOptions}
          components={{
            Option: function (props) {
              return (
                <ReactSelectCommponents.Option {...props}>
                  <div className='tw-flex tw-flex-col'>
                    <div>{props.data.label}</div>
                    <div className='tw-text-xs tw-text-gray-500'>
                      {props.data.note}
                    </div>
                  </div>
                </ReactSelectCommponents.Option>
              );
            },
          }}
          value={strategy}
          onChange={(v) => {
            if (v) setStrategy(v);
          }}
        />

        <div
          className='tw-grid tw-gap-2 tw-py-2'
          style={{
            gridTemplateColumns: '1fr 4fr',
          }}
        >
          <Select
            id='product-ranking-rule-marketplace-select'
            label='Marketplace'
            options={marketplaceOptions}
            value={marketplaceOptions[0]}
          />
          <Select
            id='product-ranking-rule-channel-select'
            label='Channel'
            isMulti
            options={channelOptions}
            value={consumerChannels}
            onChange={(mv) => {
              setConsumerChannels(
                (mv as { value: string; label: string }[]) || [],
              );
            }}
          />
        </div>

        <div
          className='tw-grid tw-gap-2 tw-py-2'
          style={{
            gridTemplateColumns: '3fr 1fr 1fr',
          }}
        >
          <Select
            id='product-ranking-rule-navigation-type'
            isMulti
            options={navigationTypeOptions}
            label='Navigation Type'
            value={navigationType}
            onChange={(mv) =>
              setNavigationType(mv as { value: string; label: string }[])
            }
          />
          <TextField
            label='Start Date'
            type='datetime-local'
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            id='product-ranking-rule-start-date'
            hasErrors={false}
          />
          <TextField
            label='End Date'
            type='datetime-local'
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            id='product-ranking-rule-end-date'
            hasErrors={false}
          />
        </div>
        <div className='tw-py-2 tw-flex'>
          <Toggle
            id='ranking-rule-trigger-apply-all'
            label='Apply to All Queries'
            size='large'
            checked={!!isApplyToAll}
            onChange={applyToAllToggleOnChange}
          />
        </div>
        <div>
          {isApplyToAll ? null : (
            <>
              <div className='tw-flex tw-items-center tw-gap-1 tw-group'>
                <h3 className='tw-my-2 tw-text-lg tw-font-bold'>
                  Trigger Concepts
                </h3>
                <IconButton
                  className='tw-invisible group-hover:tw-visible'
                  icon={<Plus width={20} height={20} />}
                  label='Add trigger concepts'
                  onClick={onTriggerMenuClick('concept')}
                />
              </div>
              {triggerModal.cascadingConcepts?.length === 0 &&
              triggerModal.exactConcepts?.length === 0 ? (
                <p className='tw-text-sm tw-text-gray-500 tw-pb-2'>
                  This is no trigger concept yet
                </p>
              ) : (
                <>
                  {triggerModal.cascadingConcepts?.length > 0 ? (
                    <TriggerConceptSection
                      items={triggerModal.cascadingConcepts}
                      title='Concept Cascading'
                      propKey='cascadingConcepts'
                    />
                  ) : null}
                  {triggerModal.exactConcepts?.length > 0 ? (
                    <TriggerConceptSection
                      items={triggerModal.exactConcepts}
                      title='Concept Exact'
                      propKey='exactConcepts'
                    />
                  ) : null}
                </>
              )}
              <div className='tw-flex tw-items-center tw-gap-1 tw-group'>
                <h3 className='tw-my-2 tw-text-lg tw-font-bold'>
                  Trigger Keywords
                </h3>
                <IconButton
                  className='tw-invisible group-hover:tw-visible'
                  icon={<Plus width={20} height={20} />}
                  label='Add trigger keywords'
                  onClick={onTriggerMenuClick('keyword')}
                />
              </div>
              {triggerModal.exactKeywords?.length === 0 &&
              triggerModal.phraseKeywords?.length === 0 &&
              triggerModal.partialKeywords?.length === 0 ? (
                <p className='tw-text-sm tw-text-gray-500 tw-pb-2'>
                  This is no trigger keyword yet
                </p>
              ) : (
                <>
                  {triggerModal.exactKeywords?.length > 0 ? (
                    <TriggerKeywordSection
                      items={triggerModal.exactKeywords}
                      title='Match Exact'
                      propKey='exactKeywords'
                    />
                  ) : null}
                  {triggerModal.partialKeywords?.length > 0 ? (
                    <TriggerKeywordSection
                      items={triggerModal.partialKeywords}
                      title='Match Partial'
                      propKey='partialKeywords'
                    />
                  ) : null}
                </>
              )}
            </>
          )}
        </div>
        <div>
          <RankingRuleBoostsOrBuries
            type={RankingRuleActionType.Boost}
            data={targetModal.boosts}
            handleItemRemove={onBoostItemRemove}
            handleItemDragDrop={(itemIdx, target) =>
              onBoostItemReOrder(itemIdx, target)
            }
            handleRemoveAll={onBoostRemoveAll}
          />
          <RankingRuleBoostsOrBuries
            type={RankingRuleActionType.Bury}
            data={targetModal.buries}
            handleItemRemove={onBuryItemRemove}
            handleItemDragDrop={(itemIdx, target) =>
              onBuryItemReOrder(itemIdx, target)
            }
            handleRemoveAll={onBuryRemoveAll}
          />
        </div>

        {validationErr.length > 0 && (
          <div className='tw-mx-10'>
            <p className='tw-text-red-700 tw-underline'>
              Data validation error:{' '}
            </p>
            <ul>
              {validationErr.map((msg, idx) => {
                return (
                  <li className='tw-text-red-700 tw-italic' key={idx}>
                    {msg}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <div className='tw-my-6 tw-flex tw-items-center tw-justify-end tw-gap-3'>
          {canWrite && (
            <Button onClick={handleSubmit} variant='primary'>
              Submit
            </Button>
          )}
          <Button onClick={handleCancel} variant='secondary'>
            Cancel
          </Button>
        </div>
      </div>
      <RankingRuleTriggerModal />
      <RankingRuleTargetModal />
      <ConfirmationModal
        isOpen={isApplyToAllConfirmModalOpen}
        onConfirm={onConfirmApplyToAllModal}
        onDismiss={onDismissApplyToAllModal}
        content={
          <p className='tw-py-2 tw-max-w-lg'>
            Enable <strong>Apply to All Queries</strong> will remove all
            existing trigger concepts and keywords
          </p>
        }
      />
      <ConfirmationModal
        isOpen={isBoostRemoveAllConfirmModalOpen}
        onConfirm={onConfirmBoostRemoveAll}
        onDismiss={onDismissBoostRemoveAll}
        content={
          <p className='tw-py-2 tw-max-w-lg'>
            <strong>All</strong> Boost targets will be removed
          </p>
        }
      />
      <ConfirmationModal
        isOpen={isBuryRemoveAllConfirmModalOpen}
        onConfirm={onConfirmBuryRemoveAll}
        onDismiss={onDismissBuryRemoveAll}
        content={
          <p className='tw-py-2 tw-max-w-lg'>
            <strong>All</strong> Bury targets will be removed
          </p>
        }
      />
    </section>
  );
}
